<template>
  <section
    class="paginate"
    :class="{
      [containerClass]: containerClass != null,
      'paginate-center': pageCount <= max,
    }"
  >
    <section
      v-if="pageCount > max"
      v-ripple
      class="paginate-home"
      @click="handleHome"
    >
      <home-page-svg />
    </section>
    <ul>
      <li
        v-if="firstLastButton"
        :class="[pageClass, firstPageSelected() ? disabledClass : '']"
      >
        <a
          :class="pageLinkClass"
          :tabindex="firstPageSelected() ? -1 : 0"
          @click="selectFirstPage()"
          @keyup.enter="selectFirstPage()"
          v-html="firstButtonText"
        ></a>
      </li>

      <li
        v-if="!(firstPageSelected() && hidePrevNext) && pageCount > max"
        :class="[prevClass, firstPageSelected() ? disabledClass : '']"
      >
        <a
          :class="prevLinkClass"
          :tabindex="firstPageSelected() ? -1 : 0"
          @click="prevPage()"
          @keyup.enter="prevPage()"
        >
          <prev-page-svg
        /></a>
      </li>

      <li
        v-for="(page, i) in pages"
        :key="i"
        :class="[
          pageClass,
          page.active ? activeClass : '',
          page.selected ? selectedClass : '',
          page.disabled ? disabledClass : '',
          page.breakView ? breakViewClass : '',
        ]"
      >
        <a v-if="page.disabled" :class="pageLinkClass" tabindex="0">{{
          page.content
        }}</a>
        <a
          v-else
          :class="pageLinkClass"
          tabindex="0"
          @click="handlePageSelected(page.index + 1)"
          @keyup.enter="handlePageSelected(page.index + 1)"
          >{{ page.content }}</a
        >
      </li>

      <li
        v-if="!(lastPageSelected() && hidePrevNext) && pageCount > max"
        :class="[nextClass, lastPageSelected() ? disabledClass : '']"
      >
        <a
          :class="nextLinkClass"
          :tabindex="lastPageSelected() ? -1 : 0"
          @click="nextPage()"
          @keyup.enter="nextPage()"
        >
          <next-page-svg />
        </a>
      </li>

      <li
        v-if="firstLastButton"
        :class="[pageClass, lastPageSelected() ? disabledClass : '']"
      >
        <a
          :class="pageLinkClass"
          :tabindex="lastPageSelected() ? -1 : 0"
          @click="selectLastPage()"
          @keyup.enter="selectLastPage()"
          v-html="lastButtonText"
        ></a>
      </li>
    </ul>
    <section v-if="pageCount > max" class="paginate-counter">
      {{ selected }} / {{ pageCount }}
    </section>
  </section>
</template>

<script>
import PrevPageSvg from './icons/PrevPageSVG.vue';
import NextPageSvg from './icons/NextPageSVG.vue';
import HomePageSvg from './icons/HomePageSVG.vue';
export default {
  components: { PrevPageSvg, NextPageSvg, HomePageSvg },
  props: {
    value: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    pageCount: {
      type: Number,
      required: true,
    },
    forcePage: {
      type: Number,
      default: null,
    },
    pageRange: {
      type: Number,
      default: 3,
    },
    marginPages: {
      type: Number,
      default: 1,
    },
    prevText: {
      type: String,
      default: 'Prev',
    },
    nextText: {
      type: String,
      default: 'Next',
    },
    containerClass: {
      type: String,
      default: null,
    },
    pageClass: {
      type: String,
      default: '',
    },
    pageLinkClass: {
      type: String,
      default: '',
    },
    prevClass: {
      type: String,
      default: '',
    },
    prevLinkClass: {
      type: String,
      default: '',
    },
    nextClass: {
      type: String,
      default: '',
    },
    nextLinkClass: {
      type: String,
      default: '',
    },
    breakViewClass: {
      type: String,
      default: '',
    },
    breakViewLinkClass: {
      type: String,
      default: '',
    },
    activeClass: {
      type: String,
      default: 'paginate-active',
    },
    selectedClass: {
      type: String,
      default: 'paginate-selected',
    },
    disabledClass: {
      type: String,
      default: 'disabled',
    },

    firstLastButton: {
      type: Boolean,
      default: false,
    },
    firstButtonText: {
      type: String,
      default: 'First',
    },
    lastButtonText: {
      type: String,
      default: 'Last',
    },
    hidePrevNext: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      innerValue: 1,
    };
  },
  computed: {
    selected: {
      get: function () {
        return this.value || this.innerValue;
      },
      set: function (newValue) {
        this.innerValue = newValue;
      },
    },
    pageCountArray() {
      return Array.from({ length: this.pageCount }, (_, i) => i + 1);
    },
    pages: function () {
      let items = [];
      if (this.pageCount <= this.max) {
        for (let index = 0; index < this.pageCount; index++) {
          const content = this.pageCountArray.at(index);
          const secondActive =
            this.selected + 1 > this.pageCount
              ? this.selected + 1 - this.pageCount
              : this.selected + 1;
          const thirdActive =
            this.selected + 2 > this.pageCount
              ? this.selected + 2 - this.pageCount
              : this.selected + 2;
          let page = {
            index: content - 1,
            content,
            selected: content === this.selected,
            activeOrder:
              content === this.selected
                ? 1
                : content === secondActive
                ? 2
                : content === thirdActive
                ? 3
                : null,
            active:
              content === this.selected ||
              content === secondActive ||
              content === thirdActive,
          };
          items.push(page);
        }
      } else {
        const halfPageRange = Math.floor(this.pageRange / 2);

        let setPageItem = (content) => {
          const secondActive =
            this.selected + 1 > this.pageCount
              ? this.selected + 1 - this.pageCount
              : this.selected + 1;
          const thirdActive =
            this.selected + 2 > this.pageCount
              ? this.selected + 2 - this.pageCount
              : this.selected + 2;

          let page = {
            index: content - 1,
            content,
            selected: content === this.selected,
            activeOrder:
              content === this.selected
                ? 1
                : content === secondActive
                ? 2
                : content === thirdActive
                ? 3
                : null,
            active:
              content === this.selected ||
              content === secondActive ||
              content === thirdActive,
          };
          items.push(page);
        };

        const startIndex = this.selected - halfPageRange - 1;
        const lastIndex = startIndex + this.pageRange;

        for (let i = startIndex; i < lastIndex; i++) {
          let pageIndex = i;
          if (pageIndex >= this.pageCount) {
            pageIndex = pageIndex - this.pageCount;
          }
          setPageItem(this.pageCountArray.at(pageIndex));
        }
      }
      return items;
    },
  },
  beforeUpdate() {
    if (this.forcePage === undefined) return;
    if (this.forcePage !== this.selected) {
      this.selected = this.forcePage;
    }
  },
  methods: {
    handleHome() {
      this.handlePageSelected(1);
    },
    handlePageSelected(selected) {
      if (this.selected === selected) return;
      this.innerValue = selected;
      this.$emit('input', selected);
      this.$nextTick(() => {
        this.$emit('onPage', {
          page: selected,
          activePages: this.pages
            .filter((page) => page.active)
            .sort((a, b) => a.activeOrder - b.activeOrder),
        });
      });
    },
    prevPage() {
      if (this.selected <= 1) this.handlePageSelected(this.pageCount);
      else this.handlePageSelected(this.selected - 1);
    },
    nextPage() {
      if (this.selected >= this.pageCount) this.handlePageSelected(1);
      else this.handlePageSelected(this.selected + 1);
    },
    firstPageSelected() {
      return this.selected === 1;
    },
    lastPageSelected() {
      return this.selected === this.pageCount || this.pageCount === 0;
    },
    selectFirstPage() {
      if (this.selected <= 1) return;
      this.handlePageSelected(1);
    },
    selectLastPage() {
      if (this.selected >= this.pageCount) return;
      this.handlePageSelected(this.pageCount);
    },
    getActivePages() {
      return this.pages
        .filter((p) => p.active)
        .sort((a, b) => a.activeOrder - b.activeOrder);
    },
  },
};
</script>

<style lang="scss" scoped>
.paginate {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 5px;
  &-center {
    justify-content: center;
  }
  a {
    cursor: pointer;
  }
  ul {
    height: 30px;
    padding-top: 6px;
  }
  li {
    display: inline;
    a {
      min-width: 26px;
      max-width: 26px;
      max-height: 20px;
      position: relative;
      float: left;
      font-size: 10px;
      margin-left: -1px;
      line-height: 1.9;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: center;
      color: #989898;
      margin-right: 4px;
    }
    &.paginate-selected {
      a {
        border-radius: 3px;
        background-color: #f4f6fc;
      }
    }
    &.paginate-active {
      a {
        color: #0075c2;
      }
    }
  }
  &-home {
    cursor: pointer;
  }
  &-counter {
    font-size: 10px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.1;
    letter-spacing: normal;
    text-align: right;
    color: #989898;
  }
}
</style>
